import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import NavBar from '../components/navbar';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { useUnpublishedChanges } from '../context/UnpublishedChangesContext';
import dataService from '../services/dataService';
import apiClient from '../services/apiClient';
import taskContextData from '../data/TaskContext.json';
import { API_CONFIG } from '../config/config';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';

// ── Date helpers ──────────────────────────────────────────────────────────────
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isSameMonth = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const startOfWeek = (date) => {
  const d = new Date(date); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d;
};
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const addMonths = (date, n) => { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; };
const formatMonthYear = (d) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const formatWeekRange = (date) => {
  const s = startOfWeek(date), e = addDays(s, 6);
  if (s.getMonth() === e.getMonth())
    return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};
const formatDayFull = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

// ── Unpublished-changes diff helpers ─────────────────────────────────────────
// Normalize a task for equality comparison; strips volatile fields (timestamps, etc.)
const _localDateStr = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const _normalizeTask = (t) => ({
  task_id:        t.task_id || '',
  title:          (t.title || '').trim(),
  status:         t.status || 'notStarted',
  due:            _localDateStr(t.due),
  notes:          (t.notes || '').trim(),
  action_items:   JSON.stringify((t.action_items || []).filter(Boolean).map(s => String(s).trim()).sort()),
  item_type:      t.item_type || '',
  start_datetime: (t.start_datetime || t.start || '').slice(0, 16),
  end_datetime:   (t.end_datetime || t.end || '').slice(0, 16),
});
// Returns a Set<task_id> of tasks that differ between currentTasks and publishedTasks.
// Includes newly added tasks and deleted tasks (their IDs are kept for the badge count).
const _computeDirtyIds = (currentTasks, publishedTasks) => {
  // Only index tasks with a valid task_id — no-ID tasks collapse to key `undefined` in a Map,
  // causing phantom DELETED entries for every no-ID baseline task.
  const pubMap = new Map(
    (publishedTasks || []).filter(t => !!t.task_id).map(t => [t.task_id, t])
  );
  const curIds = new Set();
  const dirty = new Set();
  for (const t of (currentTasks || [])) {
    const id = t.task_id;
    if (!id) continue;
    curIds.add(id);
    const pub = pubMap.get(id);
    if (!pub) { dirty.add(id); } else {
      const cn = _normalizeTask(t); const pn = _normalizeTask(pub);
      if (JSON.stringify(cn) !== JSON.stringify(pn)) {
        const diff = Object.keys(cn).filter(k => JSON.stringify(cn[k]) !== JSON.stringify(pn[k]));
        console.log('[dirty] CHANGED', id, t.title, 'fields:', diff, diff.map(k => ({ field: k, cur: cn[k], base: pn[k] })));
        dirty.add(id);
      }
    }
  }
  for (const id of pubMap.keys()) { if (!curIds.has(id)) { console.log('[dirty] DELETED', id); dirty.add(id); } }
  return dirty;
};

const _etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit', hour12: true,
});
const _parseCompactTs = (ts) => {
  const m = String(ts).match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z` : null;
};
const formatPublishedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const date = isNaN(d.getTime()) ? new Date(_parseCompactTs(iso)) : d;
  if (isNaN(date.getTime())) return null;
  try { return _etFormatter.format(date) + ' ET'; } catch { return null; }
};

const Tasks = ({ onProfileClick, onBillingClick, onSeatsUpdate }) => {
  const { user, isAdmin, canManageUsersTeams, isImpersonating, impersonatedUser } = useAuth();
  const navigate = useNavigate();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  // 'tasks_found' | 'no_task_records' | 'no_connections' | null (while loading)
  const [taskLoadStatus, setTaskLoadStatus] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  // editingTask = { task: {title, due, status, action_items}, recordIdx, taskIdx, record }
  const editingTaskKeyRef = useRef(null);
  const [editTaskExpandedItems, setEditTaskExpandedItems] = useState(new Set());
  const [publishingTitle, setPublishingTitle] = useState(null); // title currently being published
  const [deletingTitle, setDeletingTitle] = useState(null); // title currently being deleted
  const [confirmDeleteRecord, setConfirmDeleteRecord] = useState(null);
  const [confirmRemoveTask, setConfirmRemoveTask] = useState(null); // { record, taskIdx, taskTitle }
  const [completedOpenSet, setCompletedOpenSet] = useState(new Set());
  const [hoveredCircle, setHoveredCircle] = useState(null);

  // Multi-view state
  const [taskView, setTaskView] = useState('allTime');
  const [viewDate, setViewDate] = useState(new Date());

  const [publishModalRecord, setPublishModalRecord] = useState(null); // { record, eligibleUsers }
  const [publishSelectedEmails, setPublishSelectedEmails] = useState(new Set());

  // Acting user's personal calendar status — fetched once on login
  // { enabled: bool, connected: bool, provider: string|null } or null while loading
  const [selfCalendarStatus, setSelfCalendarStatus] = useState(null);

  // Shared unpublished-changes tracking (synced across Tasks / Calendar / CalendarConnection)
  const { unpublishedChanges, setUnpublishedChanges, unpublishedTaskIds, setUnpublishedTaskIds } = useUnpublishedChanges();
  // Ref always reflects latest unpublishedChanges — used in the click-capture handler
  const unpublishedChangesRef = useRef({});
  const unpublishedInitRef = useRef(false);
  // Published snapshot per title: Task[] when known, null when unknown (pre-existing unpublished from prev session).
  // Used to diff-compute dirty task IDs so edit→revert correctly shows 0 unpublished changes.
  const lastPublishedTasksRef = useRef({});
  // URL the user tried to navigate to while there were unpublished changes
  const [pendingNavUrl, setPendingNavUrl] = useState(null);
  // Titles whose unpublished cards are currently playing the attention animation
  const [flashingTitles, setFlashingTitles] = useState(new Set());
  // DOM refs for unpublished task cards, keyed as `${title}:::${task_id}`
  const unpublishedCardRefs = useRef({});

  // Ref so polling callback can read current editing state without a stale closure
  const editingTaskRef = useRef(null);
  useEffect(() => { editingTaskRef.current = editingTask; }, [editingTask]);
  useEffect(() => {
    const key = editingTask
      ? `${editingTask.record?.task_list_title || ''}:${editingTask.taskIdx ?? 'new'}`
      : null;
    if (editingTaskKeyRef.current !== key) {
      editingTaskKeyRef.current = key;
      setEditTaskExpandedItems(new Set());
    }
  }, [editingTask]);
  const pickerOpenRef = useRef(false);
  const completedSectionRefs = useRef({});

  // Connection data stored during Strategy-2 loading for the publish-modal member lookup
  const connectionsRef = useRef([]);

  // Titles deleted this session — filter them out of every poll/reload to handle eventual consistency
  const deletedTitlesRef = useRef(new Set());

  // Create-task-list modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTitle, setCreateTitle]         = useState('');
  const [createAddress, setCreateAddress]     = useState('');
  const [isCreating, setIsCreating]           = useState(false);
  const [createError, setCreateError]         = useState('');

  const canCreateTaskList = useMemo(() => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const LEAD = new Set(['team_lead', 'solo_agent', 'single_agent', 'admin']);
    return LEAD.has(user.team_role);
  }, [user, isAdmin, canManageUsersTeams]);

  // Archive / restore state
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archived'
  const [confirmArchiveRecord, setConfirmArchiveRecord] = useState(null);
  const [archivingTitle, setArchivingTitle] = useState(null);
  const [restoringTitle, setRestoringTitle] = useState(null);
  // Discard unpublished changes state
  const [confirmDiscardRecord, setConfirmDiscardRecord] = useState(null);
  const [discardingTitle, setDiscardingTitle] = useState(null);
  const [toast, setToast] = useState(null); // { message }

  const canDeleteTaskLists = useMemo(() => {
    if (user?.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const email = user?.email?.toLowerCase();
    const teamRole = user?.team_role;
    for (const conn of connectionsRef.current) {
      const m = (conn.members || []).find(m => m.email?.toLowerCase() === email);
      if (m) { const val = m.can_delete_task_lists ?? m.permissions?.can_delete_task_lists; if (val !== undefined) return Boolean(val); }
    }
    return teamRole === 'team_lead';
  }, [user, isAdmin, canManageUsersTeams, connectionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const canArchiveTaskLists = useMemo(() => {
    if (user?.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const email = user?.email?.toLowerCase();
    const teamRole = user?.team_role;
    for (const conn of connectionsRef.current) {
      const m = (conn.members || []).find(m => m.email?.toLowerCase() === email);
      if (m) {
        const val = m.can_archive_task_lists ?? m.permissions?.can_archive_task_lists;
        if (val !== undefined) return Boolean(val);
        return m.role === 'team_lead';
      }
    }
    return teamRole === 'team_lead';
  }, [user, isAdmin, canManageUsersTeams, connectionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────
  const toDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const handleOpenEdit = (task, record, recordIdx, taskIdx) => {
    setEditingTask({ task: { ...task }, originalTask: { ...task }, record, recordIdx, taskIdx });
  };

  const handleAddTask = (record, recordIdx) => {
    const blankTask = { title: '', status: 'notStarted', due: null, action_items: [] };
    setEditingTask({ task: { ...blankTask }, originalTask: { ...blankTask }, record, recordIdx, taskIdx: null, isNewTask: true });
  };

  useEffect(() => {
    if (document.getElementById('unpublished-pop-anim')) return;
    const s = document.createElement('style');
    s.id = 'unpublished-pop-anim';
    s.textContent = '@keyframes unpublishedPop { 0%{transform:scale(1);} 40%{transform:scale(1.025);box-shadow:0 0 0 4px rgba(251,146,60,0.28),0 4px 16px rgba(251,146,60,0.18);} 100%{transform:scale(1);} }';
    document.head.appendChild(s);
  }, []);

  const handleFlashUnpublished = (title) => {
    const doFlash = () => {
      const cards = Object.entries(unpublishedCardRefs.current)
        .filter(([k]) => k.startsWith(`${title}:::`))
        .map(([, el]) => el)
        .filter(Boolean);
      if (cards.length > 0) cards[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setFlashingTitles(prev => new Set([...prev, title]));
      setTimeout(() => setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; }), 800);
    };
    const unpubIds = unpublishedTaskIds[title] || new Set();
    const record = tasksData.find(r => r.task_list_title === title);
    const hasCompletedUnpublished = (record?.tasks || []).some(
      t => unpubIds.has(t.task_id) && t.status === 'completed'
    );
    if (hasCompletedUnpublished) {
      setCompletedOpenSet(prev => new Set([...prev, title]));
      setTimeout(doFlash, 50);
    } else {
      doFlash();
    }
  };

  const handleRemoveTask = (record, taskIdx) => {
    const title = record.task_list_title;
    const newTasks = (record.tasks || []).filter((_, ti) => ti !== taskIdx);
    setTasksData(prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: newTasks }
    ));
    const dirty = _computeDirtyIds(newTasks, lastPublishedTasksRef.current[title] ?? []);
    setUnpublishedTaskIds(prev => ({ ...prev, [title]: dirty }));
    setUnpublishedChanges(prev => dirty.size === 0
      ? (() => { const n = { ...prev }; delete n[title]; return n; })()
      : { ...prev, [title]: { count: dirty.size, lastEditedAt: new Date().toISOString() } }
    );
    dataService.replaceTasksDraft(title, newTasks, dirty.size > 0, dirty.size).then(ok => {
      if (!ok) console.warn('[Tasks] Remove task draft save may have failed');
    });
  };

  const handleToggleComplete = useCallback((task, record, taskIdx) => {
    const title = record.task_list_title;
    if (deletedTitlesRef.current.has(title)) return;
    const newStatus = task.status === 'completed' ? 'notStarted' : 'completed';
    const stampedId = task.task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const updatedTask = {
      ...task,
      task_id: stampedId,
      status: newStatus,
      update_timestamp: new Date().toISOString(),
    };
    // If we just assigned a task_id, register it in the baseline so the diff can detect the change
    if (!task.task_id) {
      const base = lastPublishedTasksRef.current[title];
      if (Array.isArray(base) && taskIdx >= 0 && taskIdx < base.length) {
        lastPublishedTasksRef.current[title] = base.map((bt, bi) =>
          bi === taskIdx ? { ...bt, task_id: stampedId } : bt
        );
      }
    }
    const updatedTasks = (record.tasks || []).map((t, ti) => ti === taskIdx ? updatedTask : t);
    setTasksData(prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: updatedTasks }
    ));
    const dirty = _computeDirtyIds(updatedTasks, lastPublishedTasksRef.current[title] ?? []);
    setUnpublishedTaskIds(prev => ({ ...prev, [title]: dirty }));
    setUnpublishedChanges(prev => dirty.size === 0
      ? (() => { const n = { ...prev }; delete n[title]; return n; })()
      : { ...prev, [title]: { count: dirty.size, lastEditedAt: new Date().toISOString() } }
    );
    dataService.updateTaskItemDraft(title, taskIdx, updatedTask, dirty.size > 0, dirty.size).then(ok => {
      if (!ok) console.warn('[Tasks] Toggle completion draft save may have failed');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateTaskList = async () => {
    const trimmed = createTitle.trim();
    if (!trimmed || isCreating) return;
    setIsCreating(true);
    setCreateError('');
    const result = await dataService.createTaskList(trimmed, createAddress.trim());
    setIsCreating(false);
    if (!result || result.error) {
      setCreateError(result?.error || 'Failed to create task list. Please try again.');
      return;
    }
    setTasksData(prev => [result, ...prev]);
    setTaskView('allTime');
    setShowCreateModal(false);
    setCreateTitle('');
    setCreateAddress('');
  };

  const handleTaskSave = () => {
    const { task, taskIdx, record, isNewTask } = editingTask;
    const title = record.task_list_title;

    const stampedTask = {
      ...task,
      task_id: task.task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      update_timestamp: new Date().toISOString(),
    };

    // Compute the full updated task list synchronously (React state not yet flushed)
    const currentRecord = tasksData.find(r => r.task_list_title === title);
    const updatedTasks = isNewTask
      ? [...(currentRecord?.tasks || []), stampedTask]
      : (currentRecord?.tasks || []).map((t, ti) => ti === taskIdx ? stampedTask : t);

    setEditingTask(null);

    // When a previously no-ID task gets an ID assigned, update the baseline at the same index
    // so the diff can track it going forward instead of treating it as a perpetual NEW task.
    if (!isNewTask && !task.task_id && stampedTask.task_id) {
      const base = lastPublishedTasksRef.current[title];
      if (Array.isArray(base) && taskIdx >= 0 && taskIdx < base.length) {
        lastPublishedTasksRef.current[title] = base.map((bt, bi) =>
          bi === taskIdx ? { ...bt, task_id: stampedTask.task_id } : bt
        );
      }
    }

    const dirty = _computeDirtyIds(updatedTasks, lastPublishedTasksRef.current[title] ?? []);
    setUnpublishedTaskIds(prev => ({ ...prev, [title]: dirty }));
    setUnpublishedChanges(prev => dirty.size === 0
      ? (() => { const n = { ...prev }; delete n[title]; return n; })()
      : { ...prev, [title]: { count: dirty.size, lastEditedAt: new Date().toISOString() } }
    );

    setTasksData(prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: updatedTasks }
    ));
    if (isNewTask) {
      dataService.replaceTasksDraft(title, updatedTasks, dirty.size > 0, dirty.size).then(ok => {
        if (!ok) console.warn('[Tasks] New task draft save may have failed');
      });
    } else {
      dataService.updateTaskItemDraft(title, taskIdx, stampedTask, dirty.size > 0, dirty.size).then(ok => {
        if (!ok) console.warn('[Tasks] Draft save to DynamoDB may have failed');
      });
    }
  };

  const openPublishModal = (record) => {
    const recordEmail = record.id?.toLowerCase();
    const currentEmail = user?.email?.toLowerCase();
    const userRole = user?.role;
    const isAdminActingAsOwner = isAdmin && canManageUsersTeams;

    const LEAD_ROLES = ['team_lead', 'solo_agent', 'single_agent', 'admin'];

    // Find the best connection:
    // P0 — exact match by connection_id on the record (most reliable; works for Account Owner)
    // P1 — has record owner + another member with a lead role other than current user (team context)
    // P2 — has current user + another member with a lead role other than current user
    // P3 — Account Owner may own the connection but NOT be in members[]
    // P4 — any connection containing the record owner
    // P5 — any connection containing the current user
    const teamConnection =
      (record.connection_id && connectionsRef.current.find(conn =>
        conn.connection_id === record.connection_id
      )) ||
      connectionsRef.current.find(conn => {
        const ms = conn.members || [];
        return ms.some(m => m.email?.toLowerCase() === recordEmail) &&
               ms.some(m => LEAD_ROLES.includes(m.role) && m.email?.toLowerCase() !== currentEmail);
      }) ||
      connectionsRef.current.find(conn => {
        const ms = conn.members || [];
        return ms.some(m => m.email?.toLowerCase() === currentEmail) &&
               ms.some(m => LEAD_ROLES.includes(m.role) && m.email?.toLowerCase() !== currentEmail);
      }) ||
      connectionsRef.current.find(conn => {
        const ownerEmailLower = (conn.owner?.email || '').toLowerCase();
        return ownerEmailLower === currentEmail ||
          (isAdminActingAsOwner && ownerEmailLower === (user?.owner_email || '').toLowerCase());
      }) ||
      connectionsRef.current.find(conn =>
        (conn.members || []).some(m => m.email?.toLowerCase() === recordEmail)
      ) ||
      connectionsRef.current.find(conn =>
        (conn.members || []).some(m => m.email?.toLowerCase() === currentEmail)
      );

    const allMembers = teamConnection?.members?.length ? teamConnection.members : [];

    const currentMember = allMembers.find(m => m.email?.toLowerCase() === currentEmail);
    const userConnectionRole = currentMember?.role;

    // Lead Agent = a member with a lead role who is NOT the current user
    const leadMember = allMembers.find(m =>
      LEAD_ROLES.includes(m.role) && m.email?.toLowerCase() !== currentEmail
    );
    const teamLeadEmail = leadMember?.email?.toLowerCase() || null;

    // Current user is a regular member only when someone else is the lead.
    // Admin acting as owner is never a regular member.
    const isCurrentUserMember = !!teamLeadEmail && userRole !== 'owner' && !isAdminActingAsOwner && !LEAD_ROLES.includes(userConnectionRole);

    let eligible;
    if ((userRole === 'owner' || isAdminActingAsOwner) && !isCurrentUserMember) {
      // Account Owner (or Admin acting as owner): owner entry + all team members as recipients.
      // For Admin, use the delegated owner's email (user.owner_email) so the owner's calendar is included.
      const ownerEntry = isAdminActingAsOwner
        ? { email: (user?.owner_email || '').toLowerCase(), role: 'owner', first_name: user.owner_name, last_name: user.owner_last_name }
        : { email: currentEmail };
      const teamMemberEntries = allMembers.map(m => ({ ...m }));
      eligible = [ownerEntry, ...teamMemberEntries];
    } else if (!isCurrentUserMember) {
      // Lead Agent: self is publisher; other members are selectable recipients.
      const selfEntry = allMembers.find(m => m.email?.toLowerCase() === currentEmail) || { email: currentEmail };
      const otherMembers = allMembers.filter(m => m.email?.toLowerCase() !== currentEmail);
      eligible = allMembers.length ? [selfEntry, ...otherMembers] : [{ email: record.id }];
    } else {
      // Regular member: team lead is required (locked); self is publisher.
      const selfMemberObj = allMembers.find(m => m.email?.toLowerCase() === currentEmail) || { email: currentEmail };
      eligible = [selfMemberObj, ...(leadMember ? [{ ...leadMember, lockedInfo: true }] : [])];
    }
    if (!eligible.length) eligible = [{ email: record.id }];

    // Pre-select ALL entries — locked entries are forced-checked in the UI and must also be
    // included in the set so the backend's selected_recipients filter doesn't exclude them.
    const initialSelected = new Set(
      eligible.map(m => m.email?.toLowerCase()).filter(Boolean)
    );
    setPublishSelectedEmails(initialSelected);
    setPublishModalRecord({ record, eligibleUsers: eligible, isCurrentUserMember, teamLeadEmail });
  };

  const handlePublishConfirm = async () => {
    if (!publishModalRecord) return;
    const { record } = publishModalRecord;
    const title = record.task_list_title;
    const connectionId = record.connection_id || '';
    const currentRecord = tasksData.find(r => r.task_list_title === title);
    setPublishModalRecord(null);
    setPublishingTitle(title);

    // Push the editor's latest tasks to all team members' TaskContext records
    if (currentRecord?.tasks) {
      await dataService.propagateTasks(title, currentRecord.tasks);
    }

    // Publish to all selected recipients in a single backend call.
    // Admin users act on behalf of the owner — use the record's owner email so the
    // backend can locate the task context records (which are keyed to the owner, not the admin).
    const currentEmail = user?.email?.toLowerCase();
    const publishEmail = (isAdmin && canManageUsersTeams) ? (record.id?.toLowerCase() || currentEmail) : currentEmail;
    await dataService.publishTasks(publishEmail, title, connectionId, [...publishSelectedEmails]);

    // Clear unpublished badge and card highlights for this task list
    setUnpublishedChanges(prev => { const n = { ...prev }; delete n[title]; return n; });
    setUnpublishedTaskIds(prev => { const n = { ...prev }; delete n[title]; return n; });
    setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; });
    // Snapshot published state so future edits diff against what was just published
    lastPublishedTasksRef.current[title] = (currentRecord?.tasks || []).map(t => ({ ...t }));
    setPublishingTitle(null);
  };

  const handleDelete = (record) => {
    setConfirmDeleteRecord(record);
  };

  const handleConfirmDelete = async () => {
    const record = confirmDeleteRecord;
    setConfirmDeleteRecord(null);
    const title = record.task_list_title;
    setDeletingTitle(title);
    const ok = await dataService.deleteTaskList(record.id, title);
    setDeletingTitle(null);
    if (ok) {
      deletedTitlesRef.current.add(title);
      setTasksData(prev => prev.filter(r => !deletedTitlesRef.current.has(r.task_list_title)));
    } else console.warn('[Tasks] Delete task list may have failed');
  };

  const showToast = useCallback((message) => {
    setToast({ message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleDiscard = (record) => setConfirmDiscardRecord(record);

  const handleConfirmDiscard = async () => {
    const record = confirmDiscardRecord;
    setConfirmDiscardRecord(null);
    const title = record.task_list_title;
    setDiscardingTitle(title);

    const publishedBase = lastPublishedTasksRef.current[title];
    const result = await dataService.discardDraft(
      title,
      (publishedBase !== null && publishedBase !== undefined) ? publishedBase : undefined,
    );

    if (!result.success) {
      setDiscardingTitle(null);
      showToast('Failed to discard changes. Please try again.');
      return;
    }

    const restoredTasks = result.tasks;
    lastPublishedTasksRef.current[title] = restoredTasks.map(t => ({ ...t }));
    setTasksData(prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: restoredTasks }
    ));
    setUnpublishedChanges(prev => { const n = { ...prev }; delete n[title]; return n; });
    setUnpublishedTaskIds(prev => { const n = { ...prev }; delete n[title]; return n; });
    setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; });
    setDiscardingTitle(null);
    showToast('Unshared changes discarded.');
  };

  const handleArchive = (record) => setConfirmArchiveRecord(record);

  const handleConfirmArchive = async () => {
    const record = confirmArchiveRecord;
    setConfirmArchiveRecord(null);
    const title = record.task_list_title;
    setArchivingTitle(title);
    const ok = await dataService.archiveTaskList(record.id, title);
    setArchivingTitle(null);
    if (ok) {
      setTasksData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: true } : r
      ));
      showToast('Task list archived.');
    } else console.warn('[Tasks] Archive task list may have failed');
  };

  const handleRestore = async (record) => {
    const title = record.task_list_title;
    setRestoringTitle(title);
    const ok = await dataService.restoreTaskList(record.id, title);
    setRestoringTitle(null);
    if (ok) {
      setTasksData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: false } : r
      ));
      setActiveTab('active');
      showToast('Task list restored to Active.');
    } else console.warn('[Tasks] Restore task list may have failed');
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const loadTasks = useCallback(async (silent = false) => {
    if (!user) return;
    if (!silent) setLoading(true);
    try {
      const accountType = user.accountType?.toLowerCase().replace(/ /g, '_') || '';
      const typesToLoad = accountType.includes('brokerage') || accountType === 'super'
        ? ['small_team', 'medium_team', 'large_team', 'solo_assistant', 'solo']
        : ['small_team', 'medium_team', 'large_team', 'solo_assistant', 'solo', 'trial'];

      const fetchConnections = () =>
        Promise.allSettled(typesToLoad.map(t => dataService.getConnections(t))).then(results =>
          results.flatMap(r => r.status === 'fulfilled' && Array.isArray(r.value) ? r.value : [])
        ).catch(() => []);

      // Invited members — load tasks and connections in parallel so the publish modal
      // can resolve the team lead from the connection's members list
      if (user.role === 'member' && user.email) {
        const memberEmail = user.email.trim().toLowerCase();
        const [memberResult, ownedConnections, memberOfConnections] = await Promise.all([
          dataService.getTasksByMembers([memberEmail]),
          fetchConnections(),
          dataService.getMemberTeamConnections(),
        ]);
        // Merge: own connections + team connections they belong to (deduplicated by connection_id)
        const seenIds = new Set(ownedConnections.map(c => c.connection_id));
        const merged = [...ownedConnections, ...memberOfConnections.filter(c => !seenIds.has(c.connection_id))];
        connectionsRef.current = merged;
        const tasks = memberResult?.tasks ?? [];
        applyTasks(tasks, true);
        setTaskLoadStatus(memberResult?.loadStatus ?? (tasks.length > 0 ? 'tasks_found' : 'no_task_records'));
        setConnectionsLoaded(true);
        return;
      }

      // Strategy 1: single owner-tasks endpoint — run in parallel with connection loading
      // so the publish modal has member data even when Strategy 1 is the active path
      const [ownerResult, connections] = await Promise.all([
        dataService.getOwnerTasks(),
        fetchConnections(),
      ]);
      connectionsRef.current = connections;

      // Accept Strategy 1 result unless the backend reported no connections at all —
      // in that case fall through to Strategy 2 which uses the client-side connection fetch.
      if (ownerResult !== null && ownerResult.loadStatus !== 'no_connections') {
        applyTasks(ownerResult.tasks, true);
        setTaskLoadStatus(ownerResult.loadStatus);
        setConnectionsLoaded(true);
        return;
      }

      // Strategy 2: connections already loaded above — derive email set from owned connections.
      // Also triggered when Strategy 1 returned load_status='no_connections' (user_id GSI mismatch).
      console.warn('[Tasks] /api/tasks/owner unavailable or no connections found, falling back to connections + members approach');

      const ownerEmail = user.email?.trim().toLowerCase();
      const ownedConnections = connections.filter(conn =>
        conn.owner?.email?.trim().toLowerCase() === ownerEmail ||
        (conn.members || []).some(m => m.email?.trim().toLowerCase() === ownerEmail && m.is_connection_owner)
      );

      const emailSet = new Set();
      // Always include the owner's own email so their personal TaskContext records are returned
      // even when the connection lookup finds no member rows.
      if (ownerEmail) emailSet.add(ownerEmail);
      ownedConnections.forEach(conn =>
        (conn.members || []).forEach(m => { if (m.email) emailSet.add(m.email.trim().toLowerCase()); })
      );

      if (emailSet.size === 0) {
        if (!silent) {
          setTasksData([]);
          setTaskLoadStatus('no_connections');
        }
        setConnectionsLoaded(true);
        return;
      }

      const memberResult = await dataService.getTasksByMembers([...emailSet]);
      if (memberResult !== null) {
        applyTasks(memberResult.tasks, true);
        setTaskLoadStatus(memberResult.loadStatus ?? (memberResult.tasks.length > 0 ? 'tasks_found' : 'no_task_records'));
      } else {
        // Strategy 3: local fallback
        console.warn('[Tasks] All API attempts failed, using local TaskContext data');
        const localData = taskContextData.filter(r => emailSet.has(r.id) && r.active);
        applyTasks(localData);
        setTaskLoadStatus(localData.length > 0 ? 'tasks_found' : 'no_task_records');
      }
      setConnectionsLoaded(true);
    } catch (err) {
      console.error('[Tasks] loadTasks error:', err);
      const localData = taskContextData.filter(r => r.active);
      applyTasks(localData);
      setTaskLoadStatus(localData.length > 0 ? 'tasks_found' : 'no_task_records');
      setConnectionsLoaded(true);
    } finally {
      setLoading(false);
    }

    function applyTasks(newRecords, fromAPI = false) {
      const filtered = newRecords.filter(r => !deletedTitlesRef.current.has(r.task_list_title));
      if (silent) {
        // When the API confirmed success, always apply (even if empty) so remote deletions
        // are reflected without a manual refresh. Keep prev only for local-fallback data
        // where an empty result signals a network failure, not a real empty state.
        setTasksData(prev => (fromAPI || filtered.length > 0) ? filtered : prev);
        // Recompute unpublished changes so external updates (e.g. email-action completions)
        // refresh the badge count without requiring a manual page reload.
        if (filtered.length > 0 && unpublishedInitRef.current) {
          const effectiveEmail = (isImpersonating ? impersonatedUser?.email : user?.email)?.toLowerCase();
          const pollChanges = {};
          const pollTaskIds = {};
          const backfillMap = {};
          filtered.forEach(r => {
            const title = r.task_list_title;
            if (effectiveEmail && r.id && r.id.toLowerCase() !== effectiveEmail) return;
            // DB explicitly says nothing is unpublished — clear the badge regardless of
            // task timestamps (handles records where published_at is absent).
            if (r.has_unpublished_changes === false) {
              pollChanges[title] = null;
              pollTaskIds[title] = null;
              return;
            }
            const publishedAt = r.published_at ? new Date(r.published_at) : null;
            const isUnpublishedChange = (t) =>
              !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt);
            let tasks = r.tasks || [];
            if (tasks.some(t => isUnpublishedChange(t) && !t.task_id)) {
              const existingBase = lastPublishedTasksRef.current[title] || [];
              tasks = tasks.map((t, ti) =>
                (isUnpublishedChange(t) && !t.task_id)
                  ? { ...t, task_id: (existingBase[ti] || {}).task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
                  : t
              );
              backfillMap[title] = tasks;
            }
            const dirtyTasks = tasks.filter(t => t.task_id && isUnpublishedChange(t));
            pollChanges[title] = dirtyTasks.length > 0
              ? { count: dirtyTasks.length, lastEditedAt: r.last_edited_at || null }
              : null;
            pollTaskIds[title] = dirtyTasks.length > 0
              ? new Set(dirtyTasks.map(t => t.task_id))
              : null;
          });
          setUnpublishedChanges(prev => {
            const next = { ...prev };
            Object.entries(pollChanges).forEach(([t, v]) => { if (v) next[t] = v; else delete next[t]; });
            return next;
          });
          setUnpublishedTaskIds(prev => {
            const next = { ...prev };
            Object.entries(pollTaskIds).forEach(([t, v]) => { if (v) next[t] = v; else delete next[t]; });
            return next;
          });
          if (Object.keys(backfillMap).length > 0) {
            setTasksData(prev => prev.map(r => { const bf = backfillMap[r.task_list_title]; return bf ? { ...r, tasks: bf } : r; }));
            Object.entries(backfillMap).forEach(([title, tasks]) => {
              lastPublishedTasksRef.current[title] = tasks.map(t => ({ ...t }));
            });
          }
        }
      } else {
        setTasksData(filtered);
      }
    }
  }, [user, isImpersonating, impersonatedUser, setUnpublishedChanges, setUnpublishedTaskIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // SSE: instant updates when tasks are saved through the API (local dev server supports this).
  // Skipped entirely on the Lambda backend — polling handles that case and the Lambda endpoint
  // does not return CORS headers for EventSource requests.
  useEffect(() => {
    if (!user) return;
    if (!API_CONFIG.BASE_URL.includes('localhost')) return;
    let es;
    try {
      es = new EventSource(`${API_CONFIG.BASE_URL}/api/tasks/sse`);

      es.addEventListener('task-updated', (e) => {
        try {
          const { task_list_title, task_index, task } = JSON.parse(e.data);
          setTasksData(prev => prev.map(rec =>
            rec.task_list_title !== task_list_title ? rec : {
              ...rec,
              tasks: rec.tasks.map((t, ti) => ti === task_index ? task : t)
            }
          ));
        } catch { /* malformed event — ignore */ }
      });

      es.onerror = () => { /* backend doesn't support SSE — polling covers this */ };
    } catch { /* EventSource blocked or unavailable */ }

    return () => { if (es) es.close(); };
  }, [user]);

  // Background polling: re-fetch every 10 s to pick up DynamoDB changes from any source.
  // Skips while the edit modal is open or the browser tab is hidden.
  useEffect(() => {
    if (!user) return;
    const INTERVAL = 10_000;
    let timerId = null;

    const poll = () => {
      if (!document.hidden && !editingTaskRef.current) loadTasks(true);
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerId);
        timerId = null;
      } else {
        if (!timerId) timerId = setInterval(poll, INTERVAL);
      }
    };

    timerId = setInterval(poll, INTERVAL);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, loadTasks]);

  // Initialise unpublished-changes state from the backend on first data load
  useEffect(() => {
    if (loading || unpublishedInitRef.current || tasksData.length === 0) return;
    unpublishedInitRef.current = true;
    const effectiveEmail = (isImpersonating ? impersonatedUser?.email : user?.email)?.toLowerCase();
    const initial = {};
    const initialTaskIds = {};
    const backfillMap = {};
    tasksData.forEach(r => {
      const title = r.task_list_title;
      // Skip records that belong to other users — their update_timestamp values are not this user's edits.
      if (effectiveEmail && r.id && r.id.toLowerCase() !== effectiveEmail) return;
      // A task is genuinely unpublished only if its update_timestamp is AFTER the last publish.
      // update_timestamp is never cleared by publish, so comparing against published_at is required.
      const publishedAt = r.published_at ? new Date(r.published_at) : null;
      const isUnpublishedChange = (t) =>
        !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt);
      // Backfill task_id for legacy tasks that have genuinely new unpublished changes but no task_id.
      // Reuse the stable ID already in the baseline at the same index when available — generating
      // a fresh random ID on every poll causes the previous session's ID to appear DELETED in the diff.
      let tasks = r.tasks || [];
      if (tasks.some(t => isUnpublishedChange(t) && !t.task_id)) {
        const existingBase = lastPublishedTasksRef.current[title] || [];
        tasks = tasks.map((t, ti) =>
          (isUnpublishedChange(t) && !t.task_id)
            ? { ...t, task_id: (existingBase[ti] || {}).task_id || `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
            : t
        );
        backfillMap[title] = tasks;
      }
      // Only count tasks the current user personally has pending — tasks with update_timestamp are user-specific drafts.
      // Do NOT use r.has_unpublished_changes / r.unpublished_change_count, which are shared task-list-level flags
      // that reflect any team member's changes and would bleed other users' indicators into this user's view.
      const dirtyTasks = tasks.filter(t => t.task_id && isUnpublishedChange(t));
      if (dirtyTasks.length > 0) {
        initial[title] = { count: dirtyTasks.length, lastEditedAt: r.last_edited_at || null };
        initialTaskIds[title] = new Set(dirtyTasks.map(t => t.task_id));
      }
      // Seed published snapshot using backfilled tasks so diff can track them by ID.
      if (!(title in lastPublishedTasksRef.current)) {
        lastPublishedTasksRef.current[title] = tasks.map(t => ({ ...t }));
      }
    });
    // Apply backfilled task_ids to tasksData state so cards render with their IDs.
    if (Object.keys(backfillMap).length > 0) {
      setTasksData(prev => prev.map(r => {
        const bf = backfillMap[r.task_list_title];
        return bf ? { ...r, tasks: bf } : r;
      }));
      Object.entries(backfillMap).forEach(([title, tasks]) => {
        lastPublishedTasksRef.current[title] = tasks.map(t => ({ ...t }));
      });
    }
    if (Object.keys(initial).length > 0) setUnpublishedChanges(prev => ({ ...prev, ...initial }));
    if (Object.keys(initialTaskIds).length > 0) {
      setUnpublishedTaskIds(prev => {
        const next = { ...prev };
        Object.entries(initialTaskIds).forEach(([title, ids]) => {
          next[title] = ids;
        });
        return next;
      });
    }
  }, [tasksData, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the acting user's personal calendar auth status once on login
  useEffect(() => {
    if (!user) { setSelfCalendarStatus(null); return; }
    apiClient.get('/api/calendar/auth/status').then(res => {
      setSelfCalendarStatus({
        enabled: res.data.calendar_sync_enabled !== false,
        connected: !!res.data.connected,
        provider: res.data.provider || null,
      });
    }).catch(() => {
      setSelfCalendarStatus({ enabled: false, connected: false, provider: null });
    });
  }, [user]);

  // Keep ref in sync so the click-capture handler always reads the latest value
  useEffect(() => {
    unpublishedChangesRef.current = unpublishedChanges;
  }, [unpublishedChanges]);

  // Intercept NavBar <Link> clicks before React Router handles them.
  // Works with BrowserRouter (no data router required).
  useEffect(() => {
    const handleNavClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const navbar = document.querySelector('.main-navbar');
      if (!navbar || !navbar.contains(anchor)) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href === window.location.pathname) return;
      if (Object.values(unpublishedChangesRef.current).some(v => v.count > 0)) {
        e.preventDefault();
        e.stopPropagation();
        setPendingNavUrl(href);
      }
    };
    document.addEventListener('click', handleNavClick, true);
    return () => document.removeEventListener('click', handleNavClick, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before browser close / tab close when there are unpublished changes
  useEffect(() => {
    const hasAny = Object.values(unpublishedChanges).some(v => v.count > 0);
    const handler = (e) => {
      if (!hasAny) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unpublishedChanges]);

  // ── View navigation ─────────────────────────────────────────────────────────
  const goToday = () => setViewDate(new Date());
  const goPrev = () => setViewDate(d =>
    taskView === 'month' ? addMonths(d, -1) : taskView === 'week' ? addDays(d, -7) : addDays(d, -1)
  );
  const goNext = () => setViewDate(d =>
    taskView === 'month' ? addMonths(d, 1) : taskView === 'week' ? addDays(d, 7) : addDays(d, 1)
  );
  const periodLabel = taskView === 'month' ? formatMonthYear(viewDate)
    : taskView === 'week' ? formatWeekRange(viewDate)
    : taskView === 'day' ? formatDayFull(viewDate)
    : null;
  const taskMatchesPeriod = (task) => {
    if (!task.due) return false;
    const due = new Date(task.due);
    if (taskView === 'month') return isSameMonth(due, viewDate);
    if (taskView === 'week') { const ws = startOfWeek(viewDate); return due >= ws && due < addDays(ws, 7); }
    if (taskView === 'day') return isSameDay(due, viewDate);
    return false;
  };
  const effectiveTab = canArchiveTaskLists ? activeTab : 'active';
  const displayRecords = tasksData.filter(r =>
    r.active !== false &&
    (effectiveTab === 'active' ? !r.archive : r.archive === true)
  );
  const hasVisibleTasks = taskView === 'allTime' || displayRecords.some(r => (r.tasks || []).some(taskMatchesPeriod));

  return (
    <div className="App">
      <NavBar
        onSignInClick={() => setShowAuthModal(true)}
        onProfileClick={onProfileClick}
        onBillingClick={onBillingClick}
        onSeatsUpdate={onSeatsUpdate}
      />
      <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />

      <header className="App-header" style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 0,
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Title — centered in the banner */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 900,
            }}>✓</div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>Tasks</h2>
          </div>
        </div>
        {/* Active / Archived tabs — anchored to bottom of banner, owner/lead-with-permission only */}
        {canArchiveTaskLists && <div style={{ position: 'absolute', left: 20, bottom: 0, display: 'flex' }}>
          {[['active', '✓ Active'], ['archived', '🗃️ Archived']].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: activeTab === tab
                ? (tab === 'active' ? '#dcfce7' : '#dbeafe')
                : 'rgba(0,0,0,0.07)',
              color: activeTab === tab ? (tab === 'active' ? '#15803d' : '#1d4ed8') : '#4b6360',
              borderTop: activeTab === tab
                ? (tab === 'active' ? '1px solid #86efac' : '1px solid #93c5fd')
                : '1px solid transparent',
              borderLeft: activeTab === tab
                ? (tab === 'active' ? '1px solid #86efac' : '1px solid #93c5fd')
                : '1px solid transparent',
              borderRight: activeTab === tab
                ? (tab === 'active' ? '1px solid #86efac' : '1px solid #93c5fd')
                : '1px solid transparent',
              borderBottom: activeTab === tab
                ? (tab === 'active' ? '1px solid #dcfce7' : '1px solid #dbeafe')
                : 'none',
              borderRadius: '6px 6px 0 0',
              marginBottom: activeTab === tab ? '-1px' : '0',
              transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>}
      </header>

      <Container className="mt-2" style={{ position: 'relative' }}>
        {/* View switcher toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0 10px', marginBottom: '4px', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          {canCreateTaskList && effectiveTab !== 'archived' && (
            <button
              onClick={() => { setCreateTitle(''); setCreateAddress(''); setCreateError(''); setShowCreateModal(true); }}
              style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#1967d2', color: 'white', fontWeight: 600, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1558b0'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#1967d2'; }}
            >
              + Create Task List
            </button>
          )}
          {taskView !== 'allTime' && (
            <>
              <button onClick={goToday}
                onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #dadce0', background: 'white', color: '#3c4043', fontWeight: 500, fontSize: '13px', cursor: 'pointer' }}>
                Today
              </button>
              <div style={{ display: 'flex' }}>
                {[['‹', goPrev], ['›', goNext]].map(([arrow, fn], i) => (
                  <button key={i} onClick={fn}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    style={{ width: '30px', height: '30px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#3c4043', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {arrow}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: '16px', fontWeight: 400, color: '#3c4043', flex: 1 }}>{periodLabel}</span>
            </>
          )}
          {taskView === 'allTime' && <span style={{ flex: 1 }} />}
          <div style={{ display: 'flex', borderRadius: '6px', border: '1px solid #dadce0', overflow: 'hidden' }}>
            {[['allTime', 'All Time'], ['month', 'Month'], ['week', 'Week'], ['day', 'Day']].map(([v, label], i, arr) => (
              <button key={v} onClick={() => setTaskView(v)}
                style={{ padding: '6px 12px', border: 'none', background: taskView === v ? '#e8f0fe' : 'white', color: taskView === v ? '#1967d2' : '#3c4043', fontWeight: taskView === v ? 600 : 400, fontSize: '13px', cursor: 'pointer', borderRight: i < arr.length - 1 ? '1px solid #dadce0' : 'none' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Modal-style loading popup */}
        {loading && (
          <>
            <style>{`
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
              @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
            `}</style>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: '8px', zIndex: 500, minHeight: '200px'
            }} />
            <div style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 501, backgroundColor: 'white', padding: '25px 35px', borderRadius: '12px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '2px solid #007bff', minWidth: '280px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{
                  width: '32px', height: '32px',
                  border: '4px solid #f3f3f3', borderTop: '4px solid #007bff',
                  borderRadius: '50%', animation: 'spin 1s linear infinite'
                }} />
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>Loading tasks...</span>
              </div>
              <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', animation: 'pulse 2s infinite' }}>
                Fetching your team's task lists...
              </div>
            </div>
          </>
        )}

        {/* Content */}
        {!loading && (
          <div style={{ padding: '8px 0 24px' }}>
            {tasksData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', color: '#cbd5e1' }}>—</div>
                <p style={{ fontSize: '16px', fontWeight: 500, margin: 0 }}>
                  {(() => {
                    if (!connectionsLoaded) return 'Loading connections…';
                    if (taskLoadStatus === 'no_connections') return 'No team connections found.';
                    if (taskLoadStatus === 'no_task_records') return 'Connections found but no task lists have been created yet.';
                    return 'No task lists found for any team member.';
                  })()}
                </p>
              </div>
            ) : displayRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>{activeTab === 'archived' ? '🗃️' : '—'}</div>
                <p style={{ fontSize: '16px', fontWeight: 500, margin: 0, color: activeTab === 'archived' ? '#64748b' : '#94a3b8' }}>
                  {activeTab === 'archived' ? 'No archived task lists.' : 'No active task lists.'}
                </p>
                {activeTab === 'archived' && (
                  <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '6px' }}>
                    Archived lists will appear here.
                  </p>
                )}
              </div>
            ) : !hasVisibleTasks ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', color: '#cbd5e1' }}>—</div>
                <p style={{ fontSize: '16px', fontWeight: 500, margin: 0 }}>
                  {`No tasks due in the selected ${taskView === 'day' ? 'day' : taskView === 'week' ? 'week' : 'month'}.`}
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', minWidth: 'max-content', padding: '4px 2px' }}>
                  {displayRecords.map((record, idx) => {
                    const tasks = record.tasks || [];
                    const tagged = tasks.map((task, taskIdx) => ({ task, taskIdx }));
                    const inPeriod = taskView === 'allTime' ? tagged : tagged.filter(({ task }) => taskMatchesPeriod(task));
                    if (taskView !== 'allTime' && inPeriod.length === 0) return null;
                    const completedCount = inPeriod.filter(({ task }) => task.status === 'completed').length;

                    return (
                      <div key={idx} style={{
                        width: '320px', flexShrink: 0, borderRadius: '12px', overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.13)', border: '1px solid rgba(0,0,0,0.07)'
                      }}>
                        {/* Column header */}
                        <div style={{
                          background: 'linear-gradient(145deg, #1a2540 0%, #243050 60%, #1e2d4a 100%)',
                          padding: '16px', position: 'relative', overflow: 'hidden'
                        }}>
                          <div style={{
                            position: 'absolute', top: '-24px', right: '-24px',
                            width: '90px', height: '90px', borderRadius: '50%',
                            background: 'rgba(255,255,255,0.04)', pointerEvents: 'none'
                          }} />
                          <div style={{ fontWeight: 700, fontSize: '14px', color: '#ffffff', lineHeight: '1.4', marginBottom: '4px', textAlign: 'center' }}>
                            {record.task_list_title}
                          </div>
                          <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {(() => {
                              const fromName = record.display_name || (record.record_type === 'user_projection' ? (record.source_email || record.id) : record.id);
                              const updatedAt = formatPublishedAt(record.published_at || record.last_edited_at || record.update_timestamp);
                              return (
                                <>
                                  <span style={{ fontSize: '11px', color: '#a4afbe' }}>
                                    <span style={{ color: '#7a8fa8' }}>From:</span> {fromName}
                                  </span>
                                  {updatedAt && (
                                    <span style={{ fontSize: '11px', color: '#a4afbe' }}>
                                      <span style={{ color: '#7a8fa8' }}>Last update:</span> {updatedAt}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                              {completedCount > 0 && (
                                <span
                                  onClick={() => {
                                    setCompletedOpenSet(prev => { const n = new Set(prev); n.add(record.task_list_title); return n; });
                                    setTimeout(() => completedSectionRefs.current[record.task_list_title]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(34,197,94,0.25)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                                  onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = ''; }}
                                  onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(34,197,94,0.25)'; }}
                                  style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: 'rgba(34,197,94,0.18)', color: '#86efac', fontWeight: 600, cursor: 'pointer', transition: 'transform 0.12s ease, box-shadow 0.12s ease' }}
                                >
                                  {completedCount} completed
                                </span>
                              )}
                              {(() => {
                                const info = unpublishedChanges[record.task_list_title];
                                if (!info || info.count === 0) return null;
                                const isDiscarding = discardingTitle === record.task_list_title;
                                const disableDiscard = isDiscarding || !!record.archive;
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                                    <button
                                      onClick={e => { e.stopPropagation(); handleFlashUnpublished(record.task_list_title); }}
                                      aria-label="Show unshared changes"
                                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,191,36,0.3)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                                      onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = ''; }}
                                      onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,191,36,0.3)'; }}
                                      style={{
                                        fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                                        background: 'rgba(251,191,36,0.18)', color: '#fbbf24',
                                        fontWeight: 600, border: '1px solid rgba(251,191,36,0.35)',
                                        cursor: 'pointer', fontFamily: 'inherit',
                                        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                                      }}
                                    >
                                      {info.count === 1 ? '1 unshared change' : `${info.count} unshared changes`}
                                    </button>
                                    <TeamNotificationTooltip role="discard" placement="bottom" wrapperStyle={{ display: 'inline-flex' }}>
                                      <button
                                        onClick={e => { e.stopPropagation(); handleDiscard(record); }}
                                        disabled={disableDiscard}
                                        onMouseEnter={e => { if (!disableDiscard) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(249,115,22,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = disableDiscard ? 'none' : '0 2px 8px rgba(249,115,22,0.35)'; }}
                                        onMouseDown={e => { if (!disableDiscard) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(249,115,22,0.3)'; } }}
                                        onMouseUp={e => { if (!disableDiscard) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(249,115,22,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', padding: '0 10px', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none',
                                          cursor: disableDiscard ? 'default' : 'pointer', fontFamily: 'inherit',
                                          background: disableDiscard ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #f97316, #ea580c)',
                                          color: disableDiscard ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: disableDiscard ? 'none' : '0 2px 8px rgba(249,115,22,0.35)',
                                        }}
                                      >
                                        {isDiscarding ? 'Discarding…' : 'Discard'}
                                      </button>
                                    </TeamNotificationTooltip>
                                  </div>
                                );
                              })()}
                            </div>
                            {/* Action buttons — second row */}
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
                              {activeTab === 'active' ? (<>
                                {/* Publish */}
                                {(() => {
                                  const isPublishing = publishingTitle === record.task_list_title;
                                  return (
                                    <TeamNotificationTooltip role="publish" placement="bottom" wrapperStyle={{ display: 'inline-flex' }}>
                                      <button
                                        onClick={() => openPublishModal(record)}
                                        disabled={isPublishing}
                                        onMouseEnter={e => { if (!isPublishing) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isPublishing ? 'none' : '0 2px 8px rgba(34,197,94,0.35)'; }}
                                        onMouseDown={e => { if (!isPublishing) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(34,197,94,0.3)'; } }}
                                        onMouseUp={e => { if (!isPublishing) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', width: '80px', padding: '0', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none',
                                          cursor: isPublishing ? 'default' : 'pointer',
                                          background: isPublishing ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
                                          color: isPublishing ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: isPublishing ? 'none' : '0 2px 8px rgba(34,197,94,0.35)',
                                        }}
                                      >{isPublishing ? '⏳ Sharing…' : '📅 Share'}</button>
                                    </TeamNotificationTooltip>
                                  );
                                })()}
                                {/* Archive — owner/lead-with-permission only */}
                                {canArchiveTaskLists && (() => {
                                  const isArchiving = archivingTitle === record.task_list_title;
                                  return (
                                    <TeamNotificationTooltip role="archive" placement="bottom" wrapperStyle={{ display: 'inline-flex', marginLeft: 'auto' }}>
                                      <button
                                        onClick={() => handleArchive(record)}
                                        disabled={isArchiving}
                                        onMouseEnter={e => { if (!isArchiving) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(59,130,246,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isArchiving ? 'none' : '0 2px 8px rgba(59,130,246,0.35)'; }}
                                        onMouseDown={e => { if (!isArchiving) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(59,130,246,0.3)'; } }}
                                        onMouseUp={e => { if (!isArchiving) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(59,130,246,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', minWidth: '80px', padding: '0 6px', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none', whiteSpace: 'nowrap',
                                          cursor: isArchiving ? 'default' : 'pointer',
                                          background: isArchiving ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                          color: isArchiving ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: isArchiving ? 'none' : '0 2px 8px rgba(59,130,246,0.35)',
                                        }}
                                      >{isArchiving ? '⏳ Archiving…' : '🗃️ Archive'}</button>
                                    </TeamNotificationTooltip>
                                  );
                                })()}
                                {/* Delete */}
                                {canDeleteTaskLists && (() => {
                                  const isDeleting = deletingTitle === record.task_list_title;
                                  return (
                                    <TeamNotificationTooltip role="delete" placement="bottom" wrapperStyle={{ display: 'inline-flex', marginLeft: canArchiveTaskLists ? undefined : 'auto' }}>
                                      <button
                                        onClick={() => handleDelete(record)}
                                        disabled={isDeleting}
                                        onMouseEnter={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isDeleting ? 'none' : '0 2px 8px rgba(239,68,68,0.35)'; }}
                                        onMouseDown={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(239,68,68,0.3)'; } }}
                                        onMouseUp={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', minWidth: '80px', padding: '0 6px', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none', whiteSpace: 'nowrap',
                                          cursor: isDeleting ? 'default' : 'pointer',
                                          background: isDeleting ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                          color: isDeleting ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: isDeleting ? 'none' : '0 2px 8px rgba(239,68,68,0.35)',
                                        }}
                                      >{isDeleting ? '⏳ Deleting…' : '🗑️ Delete'}</button>
                                    </TeamNotificationTooltip>
                                  );
                                })()}
                              </>) : (<>
                                {/* Unarchive (archived tab) */}
                                {(() => {
                                  const isRestoring = restoringTitle === record.task_list_title;
                                  return (
                                    <TeamNotificationTooltip role="unarchive" placement="bottom" wrapperStyle={{ display: 'inline-flex' }}>
                                      <button
                                        onClick={() => handleRestore(record)}
                                        disabled={isRestoring}
                                        onMouseEnter={e => { if (!isRestoring) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isRestoring ? 'none' : '0 2px 8px rgba(34,197,94,0.35)'; }}
                                        onMouseDown={e => { if (!isRestoring) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(34,197,94,0.3)'; } }}
                                        onMouseUp={e => { if (!isRestoring) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,197,94,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', minWidth: '90px', padding: '0 6px', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none', whiteSpace: 'nowrap',
                                          cursor: isRestoring ? 'default' : 'pointer',
                                          background: isRestoring ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
                                          color: isRestoring ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: isRestoring ? 'none' : '0 2px 8px rgba(34,197,94,0.35)',
                                        }}
                                      >{isRestoring ? '⏳ Unarchiving…' : '↩️ Unarchive'}</button>
                                    </TeamNotificationTooltip>
                                  );
                                })()}
                                {/* Delete (still available from archived tab) */}
                                {canDeleteTaskLists && (() => {
                                  const isDeleting = deletingTitle === record.task_list_title;
                                  return (
                                    <TeamNotificationTooltip role="delete" placement="bottom" wrapperStyle={{ display: 'inline-flex', marginLeft: 'auto' }}>
                                      <button
                                        onClick={() => handleDelete(record)}
                                        disabled={isDeleting}
                                        onMouseEnter={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.5)'; } }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isDeleting ? 'none' : '0 2px 8px rgba(239,68,68,0.35)'; }}
                                        onMouseDown={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(239,68,68,0.3)'; } }}
                                        onMouseUp={e => { if (!isDeleting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.5)'; } }}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          height: '24px', minWidth: '80px', padding: '0 6px', borderRadius: '8px',
                                          fontSize: '11px', fontWeight: 700, border: 'none', whiteSpace: 'nowrap',
                                          cursor: isDeleting ? 'default' : 'pointer',
                                          background: isDeleting ? 'rgba(148,163,184,0.18)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                          color: isDeleting ? '#64748b' : '#fff',
                                          letterSpacing: '0.3px', transition: 'all 0.15s ease',
                                          boxShadow: isDeleting ? 'none' : '0 2px 8px rgba(239,68,68,0.35)',
                                        }}
                                      >{isDeleting ? '⏳ Deleting…' : '🗑️ Delete'}</button>
                                    </TeamNotificationTooltip>
                                  );
                                })()}
                              </>)}
                            </div>
                          </div>
                        </div>

                        {/* Task cards */}
                        <div style={{ padding: '12px', background: '#f1f4f8', maxHeight: '70vh', overflowY: 'auto' }}>
                          {inPeriod.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '28px 0' }}>
                              <div style={{ fontSize: '28px', marginBottom: '8px', color: '#cbd5e1' }}>○</div>
                              <p style={{ color: '#94a3b8', fontSize: '13px', margin: canCreateTaskList ? '0 0 12px' : 0 }}>No tasks yet</p>
                              {canCreateTaskList && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAddTask(record, idx); }}
                                  style={{
                                    padding: '6px 16px', borderRadius: '6px', border: 'none',
                                    background: '#1967d2', color: 'white', fontWeight: 600, fontSize: '12px',
                                    cursor: 'pointer',
                                  }}>+ Add Task</button>
                              )}
                            </div>
                          ) : (() => {
                            const today = new Date();
                            const active = inPeriod
                              .filter(({ task }) => task.status !== 'completed')
                              .sort((a, b) => {
                                if (!a.task.due && !b.task.due) return 0;
                                if (!a.task.due) return 1;
                                if (!b.task.due) return -1;
                                return new Date(a.task.due) - new Date(b.task.due);
                              });
                            const completed = inPeriod.filter(({ task }) => task.status === 'completed');
                            const completedOpen = completedOpenSet.has(record.task_list_title);

                            const renderCard = ({ task, taskIdx }) => {
                              const isArchived = !!record.archive;
                              const isCompleted = task.status === 'completed';
                              const isUnpublished = !!task.task_id && !!unpublishedTaskIds[record.task_list_title]?.has(task.task_id);
                              const isFlashing = isUnpublished && flashingTitles.has(record.task_list_title);
                              const statusColor  = isCompleted ? '#22c55e' : '#94a3b8';
                              const statusBg     = isUnpublished ? '#fff7ed' : isCompleted ? '#f0fdf4' : '#ffffff';
                              const statusBorder = isUnpublished ? '#fed7aa' : isCompleted ? '#bbf7d0' : '#e2e8f0';
                              const leftAccent   = isUnpublished ? '#f97316' : statusColor;
                              const dueDate = task.due ? new Date(task.due) : null;
                              const daysUntilDue = dueDate
                                ? Math.round(
                                    (new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) -
                                     new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000
                                  )
                                : null;
                              const isOverdue = !isCompleted && daysUntilDue !== null && daysUntilDue < 0;
                              // Badge shows deadline urgency; left side shows the exact date as a quiet reference
                              const badgeText = isCompleted ? 'Completed'
                                : !dueDate ? 'No due date'
                                : isOverdue ? `! Overdue ${Math.abs(daysUntilDue)}d`
                                : daysUntilDue === 0 ? 'Due today'
                                : `Due in ${daysUntilDue}d`;
                              const badgeBg = isCompleted ? '#22c55e'
                                : !dueDate ? '#94a3b8'
                                : isOverdue ? '#ef4444'
                                : daysUntilDue === 0 ? '#f97316'
                                : '#22c55e';
                              const exactDateLabel = dueDate
                                ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '';
                              const dueLineText = isCompleted
                                ? (dueDate ? `Due: ${exactDateLabel} — Completed` : 'Completed')
                                : !dueDate ? 'No due date'
                                : `Due: ${exactDateLabel} — ${badgeText}`;
                              return (
                                <div key={taskIdx}
                                  ref={isUnpublished && task.task_id ? el => {
                                    const k = `${record.task_list_title}:::${task.task_id}`;
                                    if (el) unpublishedCardRefs.current[k] = el;
                                    else delete unpublishedCardRefs.current[k];
                                  } : undefined}
                                  onClick={isArchived ? undefined : () => handleOpenEdit(task, record, idx, taskIdx)}
                                  onMouseEnter={isArchived ? undefined : e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                  style={{
                                    background: statusBg, borderRadius: '8px', padding: '9px 11px',
                                    marginBottom: '6px', border: `1px solid ${statusBorder}`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: `3px solid ${leftAccent}`,
                                    cursor: isArchived ? 'default' : 'pointer', transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                                    animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined,
                                  }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '5px' }}>
                                    {(() => {
                                      const circleKey = `${record.task_list_title}-${taskIdx}`;
                                      const isCircleHovered = hoveredCircle === circleKey;
                                      const ghostColor = '#d1d5db';
                                      const circleBorder = isCompleted
                                        ? (isCircleHovered ? 'rgba(34,197,94,0.6)' : statusColor)
                                        : isCircleHovered ? '#22c55e'
                                        : statusColor;
                                      const circleBg = isCompleted
                                        ? (isCircleHovered ? 'rgba(34,197,94,0.65)' : statusColor)
                                        : isCircleHovered ? 'rgba(34,197,94,0.12)' : 'transparent';
                                      const circleColor = isCompleted ? 'white'
                                        : isCircleHovered ? '#22c55e' : ghostColor;
                                      return (
                                        <div style={{ position: 'relative', flexShrink: 0 }}>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleToggleComplete(task, record, taskIdx); }}
                                            onMouseEnter={() => setHoveredCircle(circleKey)}
                                            onMouseLeave={() => setHoveredCircle(null)}
                                            onFocus={() => setHoveredCircle(circleKey)}
                                            onBlur={() => setHoveredCircle(null)}
                                            aria-label={isCompleted ? 'Mark task uncompleted' : 'Mark task completed'}
                                            style={{
                                              width: '20px', height: '20px', borderRadius: '4px', padding: 0,
                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              fontSize: '11px', fontWeight: 800, outline: 'none',
                                              border: `2px solid ${circleBorder}`,
                                              background: circleBg,
                                              color: circleColor,
                                              cursor: 'pointer',
                                              transition: 'background 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.15s',
                                              transform: isCircleHovered ? 'scale(1.12)' : 'scale(1)',
                                              boxShadow: isCircleHovered ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
                                            }}
                                          >
                                            ✓
                                          </button>
                                          {isCircleHovered && (
                                            <div style={{
                                              position: 'absolute', top: '1px', left: '28px',
                                              background: 'rgba(30,41,59,0.95)', color: 'white',
                                              fontSize: '11px', fontWeight: 500,
                                              padding: '4px 8px', borderRadius: '5px',
                                              whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 100,
                                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                            }}>
                                              {isCompleted ? 'Mark uncompleted' : 'Mark completed'}
                                              <div style={{
                                                position: 'absolute', top: '50%', right: '100%',
                                                transform: 'translateY(-50%)',
                                                width: 0, height: 0,
                                                borderTop: '5px solid transparent',
                                                borderBottom: '5px solid transparent',
                                                borderRight: '5px solid rgba(30,41,59,0.95)',
                                              }} />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', lineHeight: '1.3', flex: 1, textAlign: 'left', textDecoration: isCompleted ? 'line-through' : 'none', opacity: isCompleted ? 0.6 : 1 }}>
                                      {task.title}
                                    </div>
                                    {canCreateTaskList && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setConfirmRemoveTask({ record, taskIdx, taskTitle: task.title || '' }); }}
                                        title="Remove task"
                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#ef4444'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#f87171'; }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: '16px', lineHeight: 1, padding: '2px 5px', borderRadius: '4px', flexShrink: 0, transition: 'background 0.15s, color 0.15s' }}
                                      >×</button>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'left', paddingLeft: '28px', marginBottom: task.action_items?.length > 0 ? '7px' : 0 }}>
                                    <span style={{ fontSize: '12px', color: badgeBg, fontWeight: 700 }}>
                                      {dueLineText}
                                    </span>
                                  </div>
                                  {task.action_items?.length > 0 && (
                                    <div style={{ borderTop: `1px solid ${statusBorder}`, paddingTop: '6px', marginTop: '2px' }}>
                                      {task.action_items.map((item, itemIdx) => (
                                        <div key={itemIdx} style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: '4px', marginBottom: itemIdx < task.action_items.length - 1 ? '4px' : 0 }}>
                                          <span style={{ color: statusColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.45', textAlign: 'center' }}>&#8250;</span>
                                          <span style={{ fontSize: '12px', color: '#475569', lineHeight: '1.45', textAlign: 'left' }}>{item}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            };

                            return (
                              <>
                                {active.map(renderCard)}
                                {completed.length > 0 && (
                                  <>
                                    <div
                                      ref={el => { completedSectionRefs.current[record.task_list_title] = el; }}
                                      onClick={() => setCompletedOpenSet(prev => {
                                        const n = new Set(prev);
                                        completedOpen ? n.delete(record.task_list_title) : n.add(record.task_list_title);
                                        return n;
                                      })}
                                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px', cursor: 'pointer', userSelect: 'none', borderTop: '1px solid #e2e8f0', marginTop: active.length > 0 ? '4px' : 0 }}>
                                      <span style={{ fontSize: '11px', color: '#64748b' }}>{completedOpen ? '▾' : '▸'}</span>
                                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Completed ({completed.length})</span>
                                    </div>
                                    {completedOpen && completed.map(renderCard)}
                                  </>
                                )}
                                {(() => {
                                  // Show ghost rows for tasks deleted but not yet published,
                                  // so the badge count matches what the user can see.
                                  const visibleIds = new Set(
                                    (record.tasks || []).map(t => t.task_id).filter(Boolean)
                                  );
                                  const pendingDelIds = [
                                    ...(unpublishedTaskIds[record.task_list_title] || new Set()),
                                  ].filter(id => !visibleIds.has(id));
                                  if (!pendingDelIds.length) return null;
                                  return pendingDelIds.map(id => {
                                    const original = (
                                      lastPublishedTasksRef.current[record.task_list_title] || []
                                    ).find(t => t.task_id === id);
                                    return (
                                      <div key={id} style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '6px 12px', marginBottom: 6,
                                        border: '1px dashed #fca5a5', borderRadius: 8,
                                        background: '#fef2f2',
                                      }}>
                                        <span style={{
                                          fontSize: 10, fontWeight: 700, color: '#ef4444',
                                          textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0,
                                        }}>
                                          Deleted
                                        </span>
                                        <span style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'line-through', flex: 1 }}>
                                          {original?.title || 'Task'}
                                        </span>
                                        <span style={{ fontSize: 11, color: '#f97316', fontWeight: 600 }}>
                                          pending
                                        </span>
                                      </div>
                                    );
                                  });
                                })()}
                                {canCreateTaskList && (
                                  <div style={{ paddingTop: '8px', borderTop: '1px solid #e2e8f0', marginTop: '6px', textAlign: 'center' }}>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAddTask(record, idx); }}
                                      style={{
                                        background: 'none', border: '1.5px dashed #94a3b8', borderRadius: '6px',
                                        color: '#64748b', fontSize: '12px', fontWeight: 600, padding: '4px 14px',
                                        cursor: 'pointer',
                                      }}>+ Add Task</button>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        {/* ── Edit Task Popup ─────────────────────────────────────────── */}
        {editingTask && (() => {
          const et = editingTask.task;
          const isCompleted  = et.status === 'completed';
          const statusColor  = isCompleted ? '#22c55e' : '#94a3b8';
          const statusBorder = isCompleted ? '#bbf7d0' : '#e2e8f0';

          const isDirty = editingTask.isNewTask
            ? (et.title?.trim().length > 0)
            : JSON.stringify(et) !== JSON.stringify(editingTask.originalTask);

          return (
            <>
              {/* Backdrop */}
              <div onClick={() => setEditingTask(null)} style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1000, backdropFilter: 'blur(2px)'
              }} />

              {/* Popup card */}
              <div style={{
                position: 'fixed', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1001, width: '420px', maxWidth: '94vw',
                backgroundColor: 'white', borderRadius: '14px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                border: `2px solid ${statusColor}`,
                display: 'flex', flexDirection: 'column', overflow: 'hidden'
              }}>

                {/* Card header — property context */}
                <div style={{
                  background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)',
                  padding: '14px 18px'
                }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff' }}>
                    {editingTask.record.task_list_title}
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Title row with completion checkbox */}
                  <div style={{ display: 'flex', alignItems: editingTask.isNewTask ? 'center' : 'flex-start', gap: '10px' }}>
                    {(() => {
                      const isModalHovered = hoveredCircle === '__modal__';
                      return (
                        <div style={{ position: 'relative', flexShrink: 0, marginTop: editingTask.isNewTask ? '0' : '2px' }}>
                          <button
                            onClick={() => setEditingTask(prev => ({ ...prev, task: { ...prev.task, status: prev.task.status === 'completed' ? 'notStarted' : 'completed' } }))}
                            onMouseEnter={() => setHoveredCircle('__modal__')}
                            onMouseLeave={() => setHoveredCircle(null)}
                            onFocus={() => setHoveredCircle('__modal__')}
                            onBlur={() => setHoveredCircle(null)}
                            aria-label={isCompleted ? 'Mark task uncompleted' : 'Mark task completed'}
                            style={{
                              width: '22px', height: '22px', borderRadius: '4px',
                              border: `2px solid ${isCompleted ? (isModalHovered ? 'rgba(34,197,94,0.6)' : '#22c55e') : (isModalHovered ? '#22c55e' : '#94a3b8')}`,
                              background: isCompleted ? (isModalHovered ? 'rgba(34,197,94,0.65)' : '#22c55e') : (isModalHovered ? 'rgba(34,197,94,0.12)' : 'transparent'),
                              color: isCompleted ? 'white' : (isModalHovered ? '#22c55e' : '#d1d5db'),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', fontWeight: 800, cursor: 'pointer',
                              transition: 'all 0.15s', outline: 'none', padding: 0,
                              transform: isModalHovered ? 'scale(1.12)' : 'scale(1)',
                              boxShadow: isModalHovered ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none',
                            }}
                          >
                            ✓
                          </button>
                          {isModalHovered && (
                            <div style={{
                              position: 'absolute', top: '1px', left: '28px',
                              background: 'rgba(30,41,59,0.95)', color: 'white',
                              fontSize: '11px', fontWeight: 500,
                              padding: '4px 8px', borderRadius: '5px',
                              whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 1200,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                            }}>
                              {isCompleted ? 'Mark uncompleted' : 'Mark completed'}
                              <div style={{
                                position: 'absolute', top: '50%', right: '100%',
                                transform: 'translateY(-50%)',
                                width: 0, height: 0,
                                borderTop: '5px solid transparent',
                                borderBottom: '5px solid transparent',
                                borderRight: '5px solid rgba(30,41,59,0.95)',
                              }} />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {editingTask.isNewTask ? (
                      <input
                        autoFocus
                        placeholder="Task title"
                        value={et.title}
                        onChange={e => setEditingTask(prev => ({ ...prev, task: { ...prev.task, title: e.target.value } }))}
                        style={{
                          flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b',
                          border: 'none', borderBottom: `2px solid ${statusColor}`,
                          outline: 'none', background: 'transparent',
                          fontFamily: 'inherit', lineHeight: '1.5',
                          textDecoration: isCompleted ? 'line-through' : 'none',
                          opacity: isCompleted ? 0.6 : 1,
                        }}
                      />
                    ) : (
                      <div style={{
                        flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b',
                        lineHeight: '1.35',
                        textDecoration: isCompleted ? 'line-through' : 'none',
                        opacity: isCompleted ? 0.6 : 1,
                      }}>
                        {et.title}
                      </div>
                    )}
                  </div>

                  {/* Due date — native datetime picker */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Due Date</div>
                    <input
                      type="datetime-local"
                      value={toDatetimeLocal(et.due)}
                      onClick={e => { if (pickerOpenRef.current) { e.target.blur(); } else { try { e.target.showPicker(); pickerOpenRef.current = true; } catch {} } }}
                      onBlur={() => { pickerOpenRef.current = false; }}
                      onChange={e => { setEditingTask(prev => ({ ...prev, task: { ...prev.task, due: e.target.value ? new Date(e.target.value).toISOString() : null } })); e.target.blur(); }}
                      style={{
                        width: '100%', padding: '9px 12px', borderRadius: '8px',
                        border: `1.5px solid ${statusBorder}`, background: '#f8fafc',
                        fontSize: '13px', color: '#1e293b', outline: 'none',
                        cursor: 'pointer', fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  {/* Action items — expandable + editable */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${statusBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {(et.action_items || []).map((item, i) => {
                        const isExpanded = editTaskExpandedItems.has(i);
                        return (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1fr 22px', gap: '6px', alignItems: isExpanded ? 'flex-start' : 'center' }}>
                            <span
                              onClick={() => setEditTaskExpandedItems(prev => {
                                const next = new Set(prev);
                                isExpanded ? next.delete(i) : next.add(i);
                                return next;
                              })}
                              style={{ color: statusColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.5', textAlign: 'center', cursor: 'pointer', userSelect: 'none', marginTop: isExpanded ? '2px' : 0 }}
                            >{isExpanded ? '⌄' : '›'}</span>
                            {isExpanded ? (
                              <textarea
                                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                                value={item}
                                rows={1}
                                onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                onChange={e => setEditingTask(prev => {
                                  const items = [...(prev.task.action_items || [])];
                                  items[i] = e.target.value;
                                  return { ...prev, task: { ...prev.task, action_items: items } };
                                })}
                                style={{ fontSize: '12px', color: '#1e293b', border: 'none', borderBottom: `1px solid ${statusBorder}`, background: 'transparent', outline: 'none', padding: '1px 2px', width: '100%', fontFamily: 'inherit', resize: 'none', overflow: 'hidden', lineHeight: '1.5', display: 'block' }}
                              />
                            ) : (
                              <span
                                onClick={() => setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(i); return next; })}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                style={{ fontSize: '12px', color: item ? '#1e293b' : '#cbd5e1', fontStyle: item ? 'normal' : 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', borderRadius: '3px', padding: '1px 2px', margin: '0 -2px' }}
                              >{item || 'click to edit'}</span>
                            )}
                            <button
                              onClick={() => {
                                setEditingTask(prev => {
                                  const items = (prev.task.action_items || []).filter((_, idx) => idx !== i);
                                  return { ...prev, task: { ...prev.task, action_items: items } };
                                });
                                setEditTaskExpandedItems(prev => {
                                  const next = new Set();
                                  for (const idx of prev) {
                                    if (idx < i) next.add(idx);
                                    else if (idx > i) next.add(idx - 1);
                                  }
                                  return next;
                                });
                              }}
                              title="Remove"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', lineHeight: 1, padding: 0 }}
                            >×</button>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => {
                          const newIdx = (et.action_items || []).length;
                          setEditingTask(prev => ({
                            ...prev, task: { ...prev.task, action_items: [...(prev.task.action_items || []), ''] }
                          }));
                          setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(newIdx); return next; });
                        }}
                        style={{
                          marginTop: '4px', padding: '4px 10px', borderRadius: '6px',
                          border: `1.5px dashed ${statusColor}`, background: 'transparent',
                          color: statusColor, fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                          alignSelf: 'flex-start', letterSpacing: '0.3px'
                        }}>+ Add item</button>
                    </div>
                  </div>
                </div>

                {/* Footer — Save / Cancel */}
                <div style={{
                  display: 'flex', justifyContent: 'flex-end', gap: '10px',
                  padding: '12px 20px', borderTop: '1px solid #f1f5f9',
                  background: '#fafbfc'
                }}>
                  <button onClick={() => setEditingTask(null)} style={{
                    padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                    background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px',
                    cursor: 'pointer'
                  }}>Cancel</button>
                  <button onClick={handleTaskSave} disabled={!isDirty} style={{
                    padding: '8px 24px', borderRadius: '8px', border: 'none',
                    background: isDirty ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#e2e8f0',
                    color: isDirty ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px',
                    cursor: isDirty ? 'pointer' : 'not-allowed', boxShadow: isDirty ? '0 3px 10px rgba(34,197,94,0.4)' : 'none'
                  }}>Save</button>
                </div>
              </div>
            </>
          );
        })()}

      {/* Unpublished-changes navigation warning */}
      {pendingNavUrl && (
        <>
          <div onClick={() => setPendingNavUrl(null)} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200, backdropFilter: 'blur(2px)'
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1201, width: '440px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            padding: '28px 28px 24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            border: '2px solid #f59e0b',
          }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}></div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>
              Unshared Task Changes
            </h3>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 22px' }}>
              You have unshared task changes. Your changes are saved for you, but they have not been shared with the team or calendars yet.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPendingNavUrl(null)}
                style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={() => { const url = pendingNavUrl; setPendingNavUrl(null); navigate(url); }}
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
              >Leave Without Sharing</button>
            </div>
          </div>
        </>
      )}

      {/* Create Task List modal */}
      {showCreateModal && (
        <>
          <div onClick={() => setShowCreateModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 1201, background: 'white', borderRadius: '12px', padding: '28px', width: '420px', maxWidth: 'calc(100vw - 40px)', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>Create Task List</h3>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Task List Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={createTitle}
                onChange={e => setCreateTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateTaskList()}
                placeholder="e.g. 123 Main St – Closing Checklist"
                autoFocus
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Property Address <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
              </label>
              <input
                type="text"
                value={createAddress}
                onChange={e => setCreateAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateTaskList()}
                placeholder="Defaults to list name if blank"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            {createError && <p style={{ color: '#ef4444', fontSize: '13px', margin: '-8px 0 14px' }}>{createError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowCreateModal(false)}
                style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleCreateTaskList} disabled={!createTitle.trim() || isCreating}
                style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', background: createTitle.trim() && !isCreating ? 'linear-gradient(135deg,#1967d2,#1558b0)' : '#e2e8f0', color: createTitle.trim() && !isCreating ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px', cursor: createTitle.trim() && !isCreating ? 'pointer' : 'not-allowed' }}>
                {isCreating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Publish recipient-selection modal */}
      {publishModalRecord && (() => {
        const { record, eligibleUsers } = publishModalRecord;
        const anySelected = publishSelectedEmails.size > 0;
        return (
          <>
            <div onClick={() => setPublishModalRecord(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1100, backdropFilter: 'blur(2px)'
            }} />
            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1101, width: '400px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              border: '2px solid #3b82f6',
              display: 'flex', flexDirection: 'column', overflow: 'hidden'
            }}>
              <div style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)', padding: '14px 18px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff', marginBottom: '2px' }}>📅 Share Task List</div>
                <div style={{ fontSize: '11px', color: '#a4afbe' }}>{record.task_list_title}</div>
              </div>
              <div style={{ padding: '18px 20px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 14px 0' }}>
                  Selected people will receive SmartClose task updates. Calendar sync will run only for connected calendars.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {eligibleUsers.map(member => {
                    const email = member.email?.toLowerCase();
                    const isLockedInfo = !!member.lockedInfo;
                    const isSelected = !isLockedInfo && publishSelectedEmails.has(email);
                    const displayName = [member.first_name, member.last_name].filter(Boolean).join(' ').trim() || member.display_name || member.name || email?.split('@')[0] || email;
                    const isLead = ['team_lead', 'solo_agent', 'single_agent', 'admin'].includes(member.role);
                    const isOwnerEntry = member.role === 'owner';
                    const isSelf = email === user?.email?.toLowerCase();
                    const isMember = !isSelf && !isLead && !isOwnerEntry;
                    const selfConnected = isSelf && selfCalendarStatus?.connected === true;
                    const providerLabel = selfCalendarStatus?.provider === 'google' ? 'Google'
                      : selfCalendarStatus?.provider === 'outlook' ? 'Outlook'
                      : selfCalendarStatus?.provider === 'icloud' ? 'iCloud'
                      : 'Calendar';
                    const isDisabled = isLockedInfo;

                    let rowBorder, rowBg, rowCursor;
                    if (isLockedInfo) {
                      rowBorder = '1.5px solid #bfdbfe'; rowBg = '#f0f7ff'; rowCursor = 'default';
                    } else if (selfConnected) {
                      rowBorder = `1.5px solid ${isSelected ? '#10b981' : '#d1fae5'}`; rowBg = isSelected ? '#f0fdf4' : '#fafffe'; rowCursor = 'pointer';
                    } else {
                      rowBorder = `1.5px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`; rowBg = isSelected ? '#eff6ff' : 'white'; rowCursor = 'pointer';
                    }

                    return (
                      <label key={email} style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 12px', borderRadius: '8px',
                        border: rowBorder, background: rowBg, cursor: rowCursor,
                        transition: 'all 0.15s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={isLockedInfo ? true : isSelected}
                          disabled={isDisabled}
                          onChange={isDisabled ? undefined : () => setPublishSelectedEmails(prev => {
                            const next = new Set(prev);
                            isSelected ? next.delete(email) : next.add(email);
                            return next;
                          })}
                          style={{ width: '16px', height: '16px', accentColor: isLockedInfo ? '#93c5fd' : selfConnected ? '#10b981' : '#3b82f6', cursor: isDisabled ? 'default' : 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                            {isSelf ? 'You' : displayName}
                          </div>
                          {isSelf ? (
                            selfConnected
                              ? <div style={{ fontSize: '11px', color: '#059669' }}>{providerLabel} Calendar connected</div>
                              : <div style={{ fontSize: '11px', color: '#94a3b8' }}>No calendar connected · SmartClose update only</div>
                          ) : (
                            <div style={{ fontSize: '11px', color: '#64748b', wordBreak: 'break-all' }}>{email}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          {isSelf && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
                              background: selfConnected ? '#d1fae5' : '#f1f5f9',
                              color: selfConnected ? '#065f46' : '#94a3b8' }}>
                              You
                            </span>
                          )}
                          {isOwnerEntry && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>
                              Owner
                            </span>
                          )}
                          {isMember && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#f1f5f9', color: '#64748b', whiteSpace: 'nowrap' }}>
                              Member
                            </span>
                          )}
                          {isLead && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#dbeafe', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                              Lead Agent
                            </span>
                          )}
                          {isLockedInfo && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#e0f2fe', color: '#0369a1', whiteSpace: 'nowrap' }}>
                              Will receive
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
                <button onClick={() => setPublishModalRecord(null)} style={{
                  padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                }}>Cancel</button>
                <button onClick={handlePublishConfirm} disabled={!anySelected} style={{
                  padding: '8px 24px', borderRadius: '8px', border: 'none',
                  background: anySelected ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e2e8f0',
                  color: anySelected ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px',
                  cursor: anySelected ? 'pointer' : 'not-allowed',
                  boxShadow: anySelected ? '0 3px 10px rgba(59,130,246,0.4)' : 'none'
                }}>Share</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Discard confirmation modal */}
      {confirmDiscardRecord && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmDiscardRecord ? 'show' : ''}`} style={{ display: confirmDiscardRecord ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Discard unshared changes?</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmDiscardRecord(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                <strong>"{confirmDiscardRecord?.task_list_title}"</strong> will be restored to the last shared version.
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                This will remove your unshared edits and restore the task list to the last shared version. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <button onClick={() => setConfirmDiscardRecord(null)} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                color: '#fff', boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
              }}>Cancel</button>
              <button onClick={handleConfirmDiscard} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff', boxShadow: '0 3px 5px rgba(239,68,68,0.3)'
              }}>Discard Changes</button>
            </div>
          </div>
        </div>
      </div>

      {/* Archive confirmation modal */}
      {confirmArchiveRecord && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmArchiveRecord ? 'show' : ''}`} style={{ display: confirmArchiveRecord ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Archive Task List?</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmArchiveRecord(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                <strong>"{confirmArchiveRecord?.task_list_title}"</strong> will move to Archived.
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                You can still view it later, but it will no longer appear in your Active task lists.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <button onClick={() => setConfirmArchiveRecord(null)} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                color: '#fff', boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
              }}>Cancel</button>
              <button onClick={handleConfirmArchive} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#fff', boxShadow: '0 3px 5px rgba(59,130,246,0.3)'
              }}>Archive</button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {confirmDeleteRecord && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmDeleteRecord ? 'show' : ''}`} style={{ display: confirmDeleteRecord ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Delete Task List</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmDeleteRecord(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                Are you sure you want to delete <strong>"{confirmDeleteRecord?.task_list_title}"</strong>?
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                This will remove it from all calendars and cannot be undone.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <button onClick={() => setConfirmDeleteRecord(null)} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                color: '#fff', boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
              }}>Cancel</button>
              <button onClick={handleConfirmDelete} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff', boxShadow: '0 3px 5px rgba(239,68,68,0.3)'
              }}>Delete</button>
            </div>
          </div>
        </div>
      </div>

      {/* Remove task confirmation modal */}
      {confirmRemoveTask && (
        <div className="modal-backdrop show" style={{ display: 'block' }} />
      )}
      <div className={`modal ${confirmRemoveTask ? 'show' : ''}`} style={{ display: confirmRemoveTask ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', width: '90%' }}>
          <div className="modal-content">
            <div className="modal-header" style={{ color: 'white', borderBottom: '1px solid #e9ecef', background: '#282c34da', padding: '8px 16px' }}>
              <h5 className="modal-title">Remove Task</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmRemoveTask(null)} aria-label="Close" style={{ filter: 'invert(1)' }} />
            </div>
            <div className="modal-body" style={{ background: '#fff', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif', padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#334155' }}>
                Are you sure you want to remove{confirmRemoveTask?.taskTitle ? <> <strong>"{confirmRemoveTask.taskTitle}"</strong></> : ' this task'}?
              </p>
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: '#94a3b8' }}>
                This will delete the task from the list. Publish to update calendars.
              </p>
            </div>
            <div className="modal-footer" style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef', padding: '8px 16px' }}>
              <button onClick={() => setConfirmRemoveTask(null)} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                color: '#fff', boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
              }}>Cancel</button>
              <button onClick={() => { handleRemoveTask(confirmRemoveTask.record, confirmRemoveTask.taskIdx); setConfirmRemoveTask(null); }} style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff', boxShadow: '0 3px 5px rgba(239,68,68,0.3)'
              }}>Remove</button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: '#f8fafc',
          padding: '10px 22px', borderRadius: '10px',
          fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          zIndex: 9999, pointerEvents: 'none',
          animation: 'fadeInUp 0.2s ease',
        }}>
          {toast.message}
        </div>
      )}
      <style>{`@keyframes fadeInUp { from { opacity:0; transform:translateX(-50%) translateY(8px);} to { opacity:1; transform:translateX(-50%) translateY(0);} }`}</style>

      </Container>
    </div>
  );
};

export default Tasks;
